import { Select } from '@zennolab_platform/webui-kit/dist/ui';
import { CheckboxANTD, InputANTD, RadioANTD } from '@zennolab_platform/webui-kit/dist/ui/antdComponents';
import { useFormatMessage } from '@zennolab_platform/webui-sdk/dist/hooks';
import { useId } from 'react';

import { config } from '../../config';
import { LANGUAGES } from '../../constants/announcement';
import { describeAudience, type IAudienceDraft, toRequestAudience } from '../../utils/audience';
import { Fieldset, Muted, SubFieldset } from '../formParts';
import { Toolbar } from '../layout';
import { KitTag, TariffTag, tariffTagColors } from '../tags';
import { TagClose, TargetedFields } from './styled';

/**
 * The standard webui-kit multi-select over the tariff catalogue (TARIFF_CODES). No free entry, so a typo cannot
 * target nobody; a stored code outside the catalogue still shows as a tag and can be removed.
 */
function TariffSelect({
  value,
  onChange,
  readOnly,
}: {
  value: string[];
  onChange: (value: string[]) => void;
  readOnly: boolean;
}) {
  const { t } = useFormatMessage();
  return (
    <SubFieldset>
      <legend id="audience-tariffs">
        {t({
          id: 'announcements.audience.tariffs',
          defaultMessage: 'Тарифы',
          description: 'Поле выбора тарифов аудитории',
        })}
      </legend>
      <Select
        mode="multiple"
        fullWidth
        allowClear
        aria-labelledby="audience-tariffs"
        disabled={readOnly}
        placeholder={t({
          id: 'announcements.audience.tariffHint',
          defaultMessage: 'Все тарифы',
          description: 'Плейсхолдер выбора тарифов аудитории',
        })}
        value={value}
        // Options are the same coloured tags as the chosen ones; `title` keeps the plain name for search.
        optionFilterProp="title"
        options={config.tariffCodes.map(code => ({
          label: <TariffTag code={code} />,
          title: code,
          value: code,
        }))}
        onChange={codes => onChange(codes as string[])}
        tagRender={({ value: code, closable, onClose }) => (
          <KitTag style={{ ...tariffTagColors(String(code)), marginInlineEnd: 4 }}>
            {String(code)}
            {closable && (
              <TagClose
                type="button"
                aria-label={t(
                  {
                    id: 'announcements.audience.removeTariff',
                    defaultMessage: 'Убрать тариф {code}',
                    description: 'Подпись кнопки удаления тарифа из аудитории; code — код тарифа',
                  },
                  { code: String(code) },
                )}
                onMouseDown={e => e.preventDefault()}
                onClick={onClose}
              >
                ×
              </TagClose>
            )}
          </KitTag>
        )}
      />
    </SubFieldset>
  );
}

/** "Аудитория": everyone, or tariffs AND languages AND an inclusive version range, described in words. */
export function AudienceEditor({
  value,
  onChange,
  readOnly,
}: {
  value: IAudienceDraft;
  onChange: (value: IAudienceDraft) => void;
  readOnly: boolean;
}) {
  const { t } = useFormatMessage();
  const versionHintId = useId();
  const set = <K extends keyof IAudienceDraft>(key: K, v: IAudienceDraft[K]) => onChange({ ...value, [key]: v });

  return (
    <Fieldset>
      <legend>
        {t({ id: 'announcements.audience.title', defaultMessage: 'Аудитория', description: 'Раздел формы анонса' })}
      </legend>
      <RadioANTD.Group
        optionType="button"
        name="audienceMode"
        disabled={readOnly}
        value={value.targeted ? 'selected' : 'everyone'}
        options={[
          {
            label: t({
              id: 'announcements.audience.everyone',
              defaultMessage: 'Все',
              description: 'Вариант аудитории анонса',
            }),
            value: 'everyone',
          },
          {
            label: t({
              id: 'announcements.audience.selected',
              defaultMessage: 'Выбранные',
              description: 'Вариант аудитории анонса',
            }),
            value: 'selected',
          },
        ]}
        onChange={e => set('targeted', e.target.value === 'selected')}
      />
      {/* Kept mounted and only hidden: mounting the tariff select scrolled the form to the top. */}
      <TargetedFields hidden={!value.targeted}>
        <TariffSelect readOnly={readOnly} value={value.tariffs} onChange={tariffs => set('tariffs', tariffs)} />
        <SubFieldset>
          <legend>
            {t({
              id: 'announcements.audience.languages',
              defaultMessage: 'Языки',
              description: 'Поле выбора языков аудитории',
            })}
          </legend>
          <CheckboxANTD.Group
            disabled={readOnly}
            value={value.languages}
            options={LANGUAGES.map(lang => ({ label: lang.toUpperCase(), value: lang }))}
            onChange={languages => set('languages', languages as string[])}
          />
        </SubFieldset>
        <Toolbar>
          <label>
            {t({
              id: 'announcements.audience.versionMin',
              defaultMessage: 'Версия от',
              description: 'Поле аудитории: нижняя граница версии',
            })}
            <InputANTD
              disabled={readOnly}
              placeholder="1.22"
              aria-describedby={versionHintId}
              value={value.versionMin}
              onChange={e => set('versionMin', e.target.value)}
            />
          </label>
          <label>
            {t({
              id: 'announcements.audience.versionMax',
              defaultMessage: 'Версия до',
              description: 'Поле аудитории: верхняя граница версии',
            })}
            <InputANTD
              disabled={readOnly}
              placeholder="1.30"
              aria-describedby={versionHintId}
              value={value.versionMax}
              onChange={e => set('versionMax', e.target.value)}
            />
          </label>
        </Toolbar>
        <Muted id={versionHintId}>
          {t({
            id: 'announcements.version.fieldHint',
            defaultMessage: 'Версия ZennoBrowser — как в «Настройки → Версия программы», например 1.22.0',
            description: 'Подсказка под полем версии',
          })}
        </Muted>
      </TargetedFields>
      <p>
        {describeAudience(t, toRequestAudience(value)) ||
          t({
            id: 'announcements.audience.all',
            defaultMessage: 'Все пользователи',
            description: 'Описание аудитории без условий',
          })}
      </p>
    </Fieldset>
  );
}
